import type { Highlight, Media, Message, Poll, Reaction } from '../../api/types'
import { entfernungText } from '../../lib/entfernung'
import { groesse } from '../../lib/format'
import { datumText, uhrText } from '../../lib/ortszeit'
import { Punktkarte } from './Punktkarte'
import { useTexte } from '../../texte'
import { Wetterkarte } from '../thread/Wetterkarte'
import { Tageskarte } from '../thread/Tageskarte'

/**
 * Ein Medium im Buch.
 *
 * **Ein Video wird zu seinem Standbild** — das liegt seit dem Hochladen auf
 * dem Server (`media.poster_name`, ausgeliefert über `/poster`), es muss also
 * nichts gerechnet werden. Was ein Buch aus einem Film sonst machen könnte,
 * gibt es nicht: bewegte Bilder auf Papier sind keine.
 *
 * **Und kein Verweis in die App.** Er wäre in einem verschenkten Buch tot und
 * verlangte obendrein eine Anmeldung. Stattdessen steht daneben, was es ist —
 * Dauer, Auflösung, Kodierung —, damit man das Video in der App wiederfindet.
 *
 * Sprachnachrichten haben kein Bild und werden zu einer Zeile.
 */
export function Medium({ media, reaktionen = [] }: { media: Media; reaktionen?: Reaction[] }) {
  const t = useTexte().buch
  // The emojis on this photo, on the picture like in the chat (0.36.2):
  // with four photos nobody would know otherwise which one is meant. Only
  // the signs -- the book is handed on, and who set them stays in the app.
  const marke = reaktionen.length > 0 && (
    <span className="buch__bildreaktionen">{reaktionen.map((r) => r.emoji).join(' ')}</span>
  )
  if (media.kind === 'audio') {
    return (
      <p className="buch__ton">
        {t.sprachnachricht(Math.round(media.duration_ms / 1000), groesse(media.bytes))}
      </p>
    )
  }

  if (media.kind === 'video') {
    const dauer = Math.round(media.duration_ms / 1000)
    const minuten = Math.floor(dauer / 60)
    const sekunden = String(dauer % 60).padStart(2, '0')
    return (
      <figure className="buch__bild buch__bild--video">
        <span className="buch__rahmen">
          {media.poster_url !== null ? (
            <img src={media.poster_url} alt="" loading="eager" />
          ) : (
            <div className="buch__kein-standbild">{t.ohneStandbild}</div>
          )}
          {marke}
        </span>
        <figcaption>
          {t.video(minuten, sekunden)}
          {media.height > 0 && t.videoHoehe(media.height)}
          {media.codec !== undefined && media.codec !== '' && t.videoCodec(media.codec)}
        </figcaption>
      </figure>
    )
  }

  return (
    <figure className="buch__bild">
      {/* **Der Rahmen beschneidet, nicht das Bild.** `overflow` gilt für ein
          `<img>` nicht -- es ist ein ersetztes Element. Stehen mehrere Fotos
          nebeneinander, trägt dieser Kasten das gemeinsame Format. */}
      <span className="buch__rahmen">
        <img src={media.url} alt="" loading="eager" />
        {marke}
      </span>
    </figure>
  )
}

/**
 * Wie viele Fotos nebeneinander stehen — **abgeleitet, nicht aufgezählt.**
 *
 * Die Zahl steht hier und nicht als Liste von Klassennamen im Stylesheet: eine
 * zweite Aufzählung derselben Regel läuft irgendwann auseinander, und das ist
 * in diesem Projekt schon mehrfach passiert.
 */
function spalten(anzahl: number): 1 | 2 | 3 {
  if (anzahl <= 1) return 1
  return anzahl >= 5 ? 3 : 2
}

/**
 * Die Medien einer Nachricht — als Raster und nicht als Stapel.
 *
 * Aus dem Betrieb: „die Bilder sind zu groß". Sie waren es doppelt. Jedes Foto
 * stand in voller Satzbreite, und jedes stand für sich allein untereinander --
 * ein Beitrag mit fünf Fotos war zwei Seiten. Zwei (ab fünf: drei) nebeneinander,
 * alle im selben Format, machen daraus ein paar Zeilen.
 *
 * **Der Ausschnitt ist der Preis, und er ist gewählt worden.** Im Raster wird
 * aus der Mitte beschnitten; ein einzelnes Foto steht dagegen ganz da. Wer alle
 * Fotos ungeschnitten sehen will, findet sie in der Anwendung.
 *
 * Sprachnachrichten haben kein Bild und bleiben Zeilen -- sie stehen über dem
 * Raster, wo sie beim Lesen zum Text gehören.
 */
export function Medien({ media, reactions = [] }: { media: Media[]; reactions?: Reaction[] }) {
  const toene = media.filter((m) => m.kind === 'audio')
  const bilder = media.filter((m) => m.kind !== 'audio')
  const fuer = (id: number) => reactions.filter((r) => r.media_id === id)

  return (
    <>
      {toene.map((m) => (
        <Medium key={m.id} media={m} />
      ))}
      {bilder.length > 0 && (
        <div className="buch__bilder" data-spalten={spalten(bilder.length)}>
          {bilder.map((m) => (
            <Medium key={m.id} media={m} reaktionen={fuer(m.id)} />
          ))}
        </div>
      )}
    </>
  )
}

/**
 * Ein Beitrag im Buch.
 *
 * `break-inside: avoid` steht im Stylesheet: ein Beitrag soll nicht mitten im
 * Satz auf die nächste Seite rutschen. Bei einem sehr langen Text gewinnt der
 * Browser — das ist richtig so, sonst bliebe eine halbe Seite leer.
 */
export function Beitrag({ nachricht, versatz }: { nachricht: Message; versatz: number | null }) {
  if (nachricht.deleted) return null

  return (
    <article className="buch__beitrag">
      {/* **Nur die Uhrzeit.** Den Tag sagt der Trenner darüber, und ihn an
          jeder Nachricht zu wiederholen macht aus der Angabe eine Zeile
          Rauschen. Seit 0.30.6 steht der Text mit ihr in der Blase, Fotos und
          Videos darunter ohne Rahmen -- wie in der Datei vom Server. */}
      {(nachricht.body !== '' || nachricht.media.length === 0) && (
        <div className="buch__blase">
          {nachricht.body !== '' && <p className="buch__text">{nachricht.body}</p>}
          <p className="buch__zeit">{uhrText(nachricht.created_at, versatz)}</p>
        </div>
      )}
      <Medien media={nachricht.media} reactions={nachricht.reactions} />
      {nachricht.body === '' && nachricht.media.length > 0 && (
        <p className="buch__zeit">{uhrText(nachricht.created_at, versatz)}</p>
      )}
      {/* **Die Abstimmung gehört ins Buch** — aus dem Betrieb gemeldet, sie
          fehlte dort schlicht. Sie ist Teil des Berichts wie ein Foto: „Wo
          fahren wir hin?" mit dem Ergebnis daneben erzählt eine Reise. */}
      {nachricht.poll !== null && <Abstimmung poll={nachricht.poll} />}
      {/* The weather card (0.31.0) belongs in the book like the poll: the same
          card as in the chat; the server draws it in PDF and EPUB. */}
      {(nachricht.weather ?? null) !== null && <Wetterkarte karte={nachricht.weather!} versatz={versatz} />}
      {/* The day card (#79) like the weather; its map is the post's photo above. */}
      {(nachricht.day_card ?? null) !== null && <Tageskarte karte={nachricht.day_card!} versatz={versatz} />}
      {/* Only the reactions to the post itself: the ones on a photo stand on
          that photo (0.36.2). Before, all of them stood here in one line. */}
      {nachricht.reactions.some((r) => r.media_id === 0) && (
        <p className="buch__reaktionen">
          {nachricht.reactions
            .filter((r) => r.media_id === 0)
            .map((r) => r.emoji)
            .join(' ')}
        </p>
      )}
    </article>
  )
}

/**
 * Eine Abstimmung auf Papier — Frage, Antworten, Anteile.
 *
 * **Gedruckt wird die Sicht eines Lesers, obwohl der Reisende druckt.** Der
 * Server schickt ihm mehr mit (`votes`, `voters`, `count` je Antwort), und im
 * Chat steht es auch — hier nicht. Das Buch wird weitergereicht: „Anna: Ja"
 * verriete jedem, der es in die Hand bekommt, wie eine Einzelne gestimmt hat.
 * Das ist dieselbe Grenze, an der die Antworten draußen bleiben, und dieselbe,
 * die zwei Zeilen weiter oben schon für die Reaktionen gilt — dort stehen die
 * Zeichen und nicht, wer sie gesetzt hat.
 *
 * **Ein Balken und keine Grafik.** Auf Papier ist ein Anteil eine Zahl; ein
 * gefüllter Balken kostet Druckfarbe und sagt nichts dazu. Die Zahl steht
 * rechts, wo man sie untereinander lesen kann.
 *
 * **Und die Frage steht nicht noch einmal da.** Der Server schreibt sie als
 * Text des Beitrags („Abstimmung: Wo fahren wir hin?"), damit sie in der
 * Chatliste und in der Benachrichtigung erscheint -- im Buch ist sie damit
 * eine Zeile weiter oben schon gedruckt. Hier stehen die Antworten, die dort
 * fehlten; genau die waren aus dem Betrieb gemeldet.
 */
function Abstimmung({ poll }: { poll: Poll }) {
  const t = useTexte().buch.abstimmung
  const zusatz: string[] = []
  if (poll.closed_at !== null) zusatz.push(t.geschlossen)
  if (poll.multi) zusatz.push(t.mehrfach)

  return (
    <div className="buch__abstimmung">
      <ul className="buch__abstimmung-liste">
        {poll.options.map((antwort) => (
          <li key={antwort.id}>
            <span className="buch__abstimmung-wort">{antwort.label}</span>
            {/* **Ohne Prozente steht dort nichts und nicht eine Null.** Beim
                Reisenden schickt der Server sie immer mit; die Zeile ist für
                den Fall, dass sich das je ändert -- eine erfundene Null wäre
                eine Behauptung. */}
            {antwort.percent !== undefined && (
              <span className="buch__abstimmung-anteil">{t.anteil(antwort.percent)}</span>
            )}
          </li>
        ))}
      </ul>
      {!poll.shows_percent && <p className="buch__abstimmung-zusatz">{t.ohneErgebnis}</p>}
      {zusatz.length > 0 && (
        <p className="buch__abstimmung-zusatz">{zusatz.join(t.trenner)}</p>
      )}
    </div>
  )
}

/**
 * Ein Highlight als Abschnitt.
 *
 * Die Zeit steht in der **Uhr der Reise** und nicht in der des Geräts, das
 * gerade druckt — sonst läse man jede Ankunft um Stunden verschoben. Dieselbe
 * Regel wie überall sonst (`tz_offset_min`), und der Punkt bringt seine eigene
 * mit, falls er eine hat.
 *
 * **Die Nennung des Wetterdienstes ist Pflicht**, nicht Zierde: WeatherKit
 * verlangt sie sichtbar dort, wo das Wetter steht.
 */
export function Punktseite({
  punkt,
  nummer,
  tripId,
  reiseVersatz,
  onKarteFertig,
}: {
  punkt: Highlight
  nummer: number
  /** Die Reise, deren Kartenbild der Ausschnitt trägt (#50). */
  tripId: number
  reiseVersatz: number | null
  /** Der kleine Ausschnitt meldet sich, wenn seine Kacheln da sind. */
  onKarteFertig: () => void
}) {
  const t = useTexte().buch
  const versatz = punkt.tz_offset_min ?? reiseVersatz
  const wetter = punkt.weather as { temp_c?: number; text?: string } | null
  const spots = punkt.pois ?? []

  return (
    <section className="buch__punkt">
      {/* **Der Kopf hält zusammen, was zusammengehört: Titel, Zeile, Text,
          Spots — und rechts daneben der Ausschnitt.** Aus dem Betrieb, mit
          Bildschirmfoto: die Überschrift stand allein am Fuß einer Seite und
          die Bilder auf der nächsten. */}
      <div className="buch__punkt-kopf">
        <div className="buch__punkt-worte">
          <h3 className="buch__punkt-titel">
            <span className="buch__nummer">{nummer}</span>
            {punkt.title}
          </h3>
          <p className="buch__punkt-zeile">
            {punkt.place_name !== '' && <span>{punkt.place_name}</span>}
            {punkt.happened_at !== null && <span>{datumText(punkt.happened_at, versatz)}</span>}
            {wetter?.temp_c !== undefined && (
              <span>
                {t.grad(Math.round(wetter.temp_c))}
                {wetter.text !== undefined && t.wetterWort(wetter.text)}
                <span className="buch__quelle">{t.wetterquelle}</span>
              </span>
            )}
          </p>
          {punkt.body !== '' && <p className="buch__text">{punkt.body}</p>}

          {/* **Spots & Aktivitäten — mit dem, was jemand dazu geschrieben
              hat.** Sie hingen längst am Punkt (`pois`) und standen nur in der
              Anwendung; im Buch fehlte damit genau das, was ein Reisebericht
              erzählt: in *welchem* Lokal, *welches* Museum. Aus dem Betrieb
              nachgefordert. Der Abstand steht dabei, weil ein Museum drei
              Straßen weiter etwas anderes ist als das Lokal nebenan. */}
          {spots.length > 0 && (
            <ul className="buch__spots">
              {spots.map((spot) => (
                <li key={spot.id}>
                  <span className="buch__spot-name">{spot.name}</span>
                  <span className="buch__spot-weit"> · {entfernungText(spot.distance_m)}</span>
                  {spot.note !== null && spot.note !== '' && (
                    <span className="buch__spot-notiz">{spot.note}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <Punktkarte
          tripId={tripId}
          punktId={punkt.id}
          lat={punkt.lat}
          lon={punkt.lon}
          nummer={nummer}
          titel={punkt.title}
          onFertig={onKarteFertig}
        />
      </div>

      <Medien media={punkt.media} />
    </section>
  )
}
